import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})

export class DashboardService {

  private baseUrl: string;
  private topics$: Observable<Object>;
  private adminProfileCache$: Observable<any> | null = null;
  private logoCache$: Observable<any> | null = null;
  private reminderTemplatesCache$: Observable<any> | null = null;

  constructor(private http: HttpClient, @Optional() @Inject('BASE_API_URL') baseUrl?: string) {
    this.baseUrl = baseUrl ? baseUrl + '' : '';
  }

  getUserProfile(): Observable<any> {
    const url = `${this.baseUrl}enterprise/admin-profile`;
    return this.http.get(url);
  }

  getAdminProfile(headers?: any): Observable<any> {
    if (!this.adminProfileCache$) {
      const url = `${this.baseUrl}enterprise/company-profile`;
      const options = headers ? { headers } : {};
      this.adminProfileCache$ = this.http.get(url, options).pipe(shareReplay(1));
    }
    return this.adminProfileCache$;
  }

  clearAdminProfileCache(): void {
    this.adminProfileCache$ = null;
  }

  clearLogoCache(): void {
    this.logoCache$ = null;
  }

  /** invite Admins */
  inviteAdmins(id, data): Observable<any> {
    const url = `${this.baseUrl}enterprise/${id}/invite-admins`;
    return this.http.post(url, data);
  }

  unInviteAdmins(id, data): Observable<any> {
    const url = `${this.baseUrl}enterprise/${id}/uninvite-admins`;
    return this.http.post(url, data);
  }

  /** update Userprofile request */
  updateUserProfile(data: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/admin-profile`;
    return this.http.put(url, data);
  }

  /** update Company request */
  updateCompanyProfile(data: any, headers?: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/company-profile`;
    const options = headers ? { headers } : {};
    return this.http.put(url, data, options);
  }

  /** get filter values */
  getFilterValues(): Observable<any> {
    const url = this.baseUrl + 'enterprise/questions/get-filters';
    return this.http.get(url);
  }

  // create question.
  createQuestion(data: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/create`;
    const payload = { ...data, source: data?.source ?? 'human' };
    return this.http.post(url, payload);
  }

  // Re-generate Question: POST only
  reGenerateQuestion(questionId: string, headers?: any, additionalPrompts: string[] = []): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/${questionId}/regenerate-question`;
    const options = headers ? { headers } : {};
    return this.http.post(url, { additionalPrompts }, options);
  }

  // Copy question.
  CopyQuestion(data: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/copy`;
    return this.http.post(url, data);
  }

  /** get questions list */
  getQuestions(page: string, type: string, req: any, headers?: any): Observable<any> {
    let reqObj = { ...req, questionType: type, page, limit: 10 };
    const url = this.baseUrl + `enterprise/questions/list`;

    // Add skip-loader header to prevent showing loader for this API call
    const finalHeaders = { 'skip-loader': 'true', ...headers };
    const options = Object.keys(finalHeaders).length > 0 ? { headers: finalHeaders } : {};

    return this.http.post(url, reqObj, options);
  }

  /** edit question */
  updateQuestion(data: any, id: string): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/`;
    return this.http.put(url + id, data);
  }

  /** delete question */
  deleteQuestion(id): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/`;
    return this.http.delete(url + id);
  }

  // Delete multiple questions
  deleteMultipleQuestions(ids: string[]): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/delete-multiple`;
    return this.http.post(url, { ids });
  }

  activityLog(filters): Observable<any> {
    const url = `${this.baseUrl}enterprise/activity-log?${filters}`;
    return this.http.get(url);
  }

  getNotifications(page): Observable<any> {
    const url = `${this.baseUrl}enterprise/notifications?page=${page}&limit=10`;
    return this.http.get(url);
  }

  // Mark specific notifications as read/unread
  markNotifications(ids: string[], read: boolean): Observable<any> {
    const url = `${this.baseUrl}enterprise/notifications/mark`;
    return this.http.post(url, { ids, read }, { headers: { 'skip-loader': 'true' } });
  }

  // Mark all notifications as read, optional scenarios comma-separated
  markAllNotificationsRead(scenarios?: string): Observable<any> {
    const params = scenarios ? `?scenarios=${encodeURIComponent(scenarios)}` : '';
    const url = `${this.baseUrl}enterprise/notifications/mark-all-read${params}`;
    return this.http.post(url, {}, { headers: { 'skip-loader': 'true' } });
  }

  // Delete notifications by ids (soft delete)
  deleteNotifications(ids: string[]): Observable<any> {
    const url = `${this.baseUrl}enterprise/notifications/delete`;
    return this.http.post(url, { ids }, { headers: { 'skip-loader': 'true' } });
  }

  /** get Assessment Stats */
  getAssessmentStats(filterVal): Observable<any> {
    const url = `${this.baseUrl}enterprise/assessments/status-based?${filterVal}`;
    return this.http.get(url);
  }

  /** get All templates */
  getAllTemplates(params: any, headers?: any): Observable<any> {
    const url = `${this.baseUrl}templates/list`;
    const options = headers ? { params, headers } : { params };
    return this.http.get(url, options);
  }

  /** Create welcome and Email Template */
  createTemplate(data): Observable<any> {
    const url = `${this.baseUrl}templates/create`;
    return this.http.post(url, data);
  }

  /** Update welcome and Email Template */
  updateTemplate(id, data: any): Observable<any> {
    const url = `${this.baseUrl}templates`;
    return this.http.put(url + '/' + id, data);
  }

  /** Undo welcome and Email Template */
  undoTemplate(id, headers?: any): Observable<any> {
    const url = `${this.baseUrl}templates/${id}/undo-delete`;
    const options = headers ? { headers } : {};
    return this.http.put(url, null, options);
  }

  /** Delete welcome and Email Template */
  deleteTemplate(id, headers?: any): Observable<any> {
    const url = `${this.baseUrl}templates`;
    const options = headers ? { headers } : {};
    return this.http.delete(url + '/' + id, options);
  }

  /** get All templates */
  getLogo(): Observable<any> {
    if (!this.logoCache$) {
      const url = `${this.baseUrl}enterprise/get-logo`;
      this.logoCache$ = this.http.get(url).pipe(shareReplay(1));
    }
    return this.logoCache$;
  }

  /** get topics for enterprise (new API) */
  getEnterpriseTopics(params?: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/topic/enterprise-only`;
    return this.http.get(url, { params, headers: { 'skip-loader': 'true' } });
  }

  // Fetch admin-configured common prompts (e.g., hints)
  getPromptHints(params: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/prompt-hints`;
    return this.http.get(url, { params, headers: { 'skip-loader': 'true' } });
  }

  getSubTopics(obj): Observable<any> {
    const url = `${this.baseUrl}enterprise/sub-topics/list?page=${obj.pageNo}&limit=${obj.limit}&search=${obj.searchWord}&topicIds=${obj.topicIds}`;
    this.topics$ = this.http.get(url);
    return this.http.get(url, { headers: { 'skip-loader': 'true' } });
  }

  /**Get Language Sub Topics */
  getLanguageSubTopics(obj): Observable<any> {
    const url = `${this.baseUrl}enterprise/coding-subtopics/list?search=${obj.searchWord}&languageIds=${obj.topicIds}&type=unique`;
    return this.http.get(url);
  }

  /** run test case for  coding question */
  runTests(data): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/run-tests/v1`;
    return this.http.post(url, data, { headers: { 'skip-loader': 'true' } });
  }

  /** Delete language details from coding question */

  deleteLanguagesFromCQ(data, id): Observable<any> {
    const url = `${this.baseUrl}enterprise/questions/${id}/delete-language`;
    return this.http.post(url, data);
  }

  getInvoices(): Observable<any> {
    const url = `${this.baseUrl}subscriptions/invoices`;
    return this.http.get(url);
  }

  cancelSubscription(): Observable<any> {
    const url = `${this.baseUrl}subscriptions/cancel`;
    return this.http.get(url);
  }

  getTopicsUsedInQuestions(): Observable<any> {
    const url = `${this.baseUrl}enterprise/topic/used-in-questions`;
    return this.http.get(url);
  }

  // Reminder Templates (Backend: /enterprise/reminder-templates)
  getReminderTemplates(): Observable<any> {
    if (!this.reminderTemplatesCache$) {
      const url = `${this.baseUrl}enterprise/reminder-templates`;
      this.reminderTemplatesCache$ = this.http.get(url).pipe(shareReplay(1));
    }
    return this.reminderTemplatesCache$;
  }

  createReminderTemplate(data: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/reminder-templates`;
    this.reminderTemplatesCache$ = null; // Clear cache
    return this.http.post(url, data);
  }

  updateReminderTemplate(id: string, data: any): Observable<any> {
    const url = `${this.baseUrl}enterprise/reminder-templates/${id}`;
    this.reminderTemplatesCache$ = null; // Clear cache
    return this.http.put(url, data);
  }

  deleteReminderTemplate(id: string): Observable<any> {
    const url = `${this.baseUrl}enterprise/reminder-templates/${id}`;
    this.reminderTemplatesCache$ = null; // Clear cache
    return this.http.delete(url);
  }

  getAvailableReminderEmailTemplates(): Observable<any> {
    const url = `${this.baseUrl}enterprise/reminder-templates/email`;
    return this.http.get(url);
  }

  getAvailableReminderWhatsappTemplates(): Observable<any> {
    const url = `${this.baseUrl}enterprise/reminder-templates/whatsapp`;
    return this.http.get(url);
  }

  triggerManualReminder(data: { invitationId: string, reminderTemplateId: string }): Observable<any> {
    const url = `${this.baseUrl}enterprise/reminders/trigger-manual`;
    return this.http.post(url, data);
  }

}
